import { useCallback } from 'react';
import { useDAWStore } from '../store/daw-store';

interface PanelResizeConfig {
  panelId: string;
  minSize: number;
  currentSize: number;
  onResize: (newSize: number) => void;
}

/**
 * Hook for managing panel resize with minimum size constraints
 * Requirements: 8.2, 8.3, 8.4 - Update panel sizes on drag, maintain minimum sizes, save sizes to current layout
 */
export function usePanelResize({ panelId, minSize, currentSize, onResize }: PanelResizeConfig) {
  const setPanelSize = useDAWStore((state) => state.setPanelSize);
  const currentLayout = useDAWStore((state) => state.currentLayout);
  const setLayout = useDAWStore((state) => state.setLayout);

  const handleResize = useCallback((delta: number) => {
    // Calculate new size
    const newSize = Math.max(minSize, currentSize + delta);
    
    // Update the panel size
    onResize(newSize);
  }, [currentSize, minSize, onResize]);

  const handleResizeEnd = useCallback(() => {
    // Save the new size to the panel sizes state (Requirements 8.4)
    setPanelSize(panelId, currentSize);
    
    // Update the current layout with new panel sizes (Requirements 8.4)
    const updatedLayout = {
      ...currentLayout,
      savedPanelSizes: {
        ...currentLayout.savedPanelSizes,
        [panelId]: currentSize,
      },
    };
    
    // This will persist to localStorage and sync to backend
    setLayout(updatedLayout);
  }, [panelId, currentSize, setPanelSize, currentLayout, setLayout]);

  return {
    handleResize,
    handleResizeEnd,
  };
}

/**
 * Minimum sizes for different panels (Requirements 8.3)
 */
export const PANEL_MIN_SIZES = {
  browser: 200,
  mixer: 300,
  channelRack: 200,
  effects: 200,
} as const;
